import { useMemo, useState } from 'react';
import { Box, Collapse, Drawer, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ResizeHandle } from '../../../shared/components/ResizeHandle';
import { SectionCollapseButton, useSectionCollapse } from '../../../shared/components/SectionCollapseButton';
import { useLocalStorageState } from '../../../shared/hooks/useLocalStorageState';
import type { EntityTypeEnrichment, EntityTypeSkill } from '../../../core/types/data-models';
import type { CardLayout } from './CardLayoutToggle';
import { EntityDetailPanel } from './EntityDetailPanel';
import { EntityKnowledgeCard } from './EntityKnowledgeCard';
import { SelectEntityDialog } from './SelectEntityDialog';
import { SkillCard } from './SkillCard';
import type { EntityCardData } from './useEntityTypeEntities';

/** Detail panel width bounds — the user drags between these; the drawer below lg is also capped at the viewport. */
const DETAIL_PANEL_MIN_WIDTH = 320;
const DETAIL_PANEL_DEFAULT_WIDTH = 400;
const DETAIL_PANEL_MAX_WIDTH = 640;

interface MainTabProps {
  skills: EntityTypeSkill[];
  /**
   * Every insight topic generated for this type — ENTITY_TYPE-scope topics
   * (entityId null, one per skill) and ENTITY-scope topics (entityId set,
   * one per entity per skill) together. Split internally: the skill grid
   * only matches ENTITY_TYPE-scope topics by skillId, and each entity's card
   * and detail panel only see the ENTITY-scope topics with their own id.
   */
  topics: EntityTypeEnrichment[];
  entities: EntityCardData[];
  entitiesLoading: boolean;
  /** Human-readable entity type, for the empty state. */
  typeLabel: string;
  layout: CardLayout;
  /** Whether the caller may flip an entity's managed flag. */
  canEdit: boolean;
  canRunSkills: boolean;
  /** The skill whose insight run is in flight, if any. */
  runningSkillId: number | null;
  /** Any project job is in flight — only one can run at a time, so every Run button is disabled. */
  runDisabled: boolean;
  /** `entityId` is passed for an ENTITY-scope skill, once the user picks one in the dialog below. */
  onRunSkill: (skillId: number, entityId?: number) => void;
  /** Opens the topic as its own tab. */
  onViewTopic: (topicId: number) => void;
  /** Persists an entity's managed flag; rejects on failure. */
  onManagedChange: (entityId: number, isManaged: boolean) => Promise<void>;
  /**
   * The entity whose detail panel is open. Owned by the parent so the panel
   * is still open when the user returns from a tab opened out of it.
   */
  selectedEntityId: number | null;
  onSelectEntity: (entityId: number | null) => void;
}

/**
 * "Main" tab — the type's skills as a collapsible grid of analysis cards,
 * then every entity of the type as knowledge cards in the chosen layout.
 * Selecting a card opens EntityDetailPanel beside the cards (or as a
 * right-hand drawer on narrow screens), resizable from its left edge.
 */
export function MainTab({
  skills,
  topics,
  entities,
  entitiesLoading,
  typeLabel,
  layout,
  canEdit,
  canRunSkills,
  runningSkillId,
  runDisabled,
  onRunSkill,
  onViewTopic,
  onManagedChange,
  selectedEntityId,
  onSelectEntity: setSelectedEntityId,
}: MainTabProps) {
  const [analysesExpanded, toggleAnalysesExpanded] = useSectionCollapse('managedEntityTypeAnalyses');
  // A layout preference, remembered per browser — this tab also remounts on
  // every tab switch, which would otherwise reset the width.
  const [panelWidth, setPanelWidth] = useLocalStorageState<number>(
    'managedEntityType.detailPanelWidth',
    DETAIL_PANEL_DEFAULT_WIDTH,
  );
  // ENTITY-scope skill the user clicked "Run…" on — opens the entity picker
  // below rather than running immediately.
  const [entityPickerSkill, setEntityPickerSkill] = useState<EntityTypeSkill | null>(null);
  // Derived rather than synced: if the list changes under the selection
  // (another entity type, a refetch), a stale id simply closes the panel.
  const selectedEntity = entities.find((e) => e.id === selectedEntityId) ?? null;
  const selectableEntityIds = useMemo(() => new Set(entities.map((e) => e.id)), [entities]);
  // An ENTITY-scope skill card's "X of Y" progress only counts managed
  // entities — unmanaged ones are grayed out and not expected to be analyzed.
  const managedEntityIds = useMemo(
    () => new Set(entities.filter((e) => e.isManaged).map((e) => e.id)),
    [entities],
  );
  const theme = useTheme();
  // Below lg the 270px nav sidebar plus this panel would leave the cards
  // almost no width, so the panel becomes a drawer instead.
  const narrow = useMediaQuery(theme.breakpoints.down('lg'));

  // `topics` mixes ENTITY_TYPE-scope topics (entityId null, one per skill)
  // with ENTITY-scope topics (entityId set, one per entity per skill). The
  // skill grid below only ever matches a skill to a type-level topic; an
  // entity-scope skill's topics are looked up per entity instead.
  const typeTopics = useMemo(() => topics.filter((t) => t.entityId == null), [topics]);

  // Analyses grid order: every ENTITY_TYPE-scope skill (in the catalog's own
  // order) before every ENTITY-scope skill — a stable sort keeps each
  // group's relative order from `skills` (already sorted by the skill's own
  // `order`) unchanged.
  const sortedSkills = useMemo(
    () => [...skills].sort((a, b) => Number(a.scope === 'ENTITY') - Number(b.scope === 'ENTITY')),
    [skills],
  );

  // Card grid order: managed entities first, then unmanaged ones (shown
  // disabled/grayed by EntityKnowledgeCard) — a stable sort keeps each
  // group's existing (alphabetical) order from `entities` unchanged.
  const sortedEntities = useMemo(
    () => [...entities].sort((a, b) => Number(b.isManaged) - Number(a.isManaged)),
    [entities],
  );

  const detailPanel = selectedEntity && (
    <EntityDetailPanel
      entity={selectedEntity}
      topics={typeTopics}
      entityTopics={topics.filter((t) => t.entityId === selectedEntity.id)}
      selectableEntityIds={selectableEntityIds}
      onSelectEntity={setSelectedEntityId}
      onViewTopic={onViewTopic}
      onClose={() => setSelectedEntityId(null)}
    />
  );

  // A flex sibling of the panel's scrolling body in both the inline panel and
  // the drawer, so it stays full height however far the panel scrolls.
  const resizeHandle = (
    <ResizeHandle
      width={panelWidth}
      minWidth={DETAIL_PANEL_MIN_WIDTH}
      maxWidth={DETAIL_PANEL_MAX_WIDTH}
      onWidthChange={setPanelWidth}
      label="Resize entity details"
      sx={{ mr: 0.5 }}
    />
  );

  const renderCard = (e: EntityCardData) => (
    <EntityKnowledgeCard
      key={e.identifier}
      entity={e}
      canEdit={canEdit}
      selected={e.id === selectedEntityId}
      analysisCount={topics.filter((t) => t.entityId === e.id).length}
      onSelect={() => setSelectedEntityId(e.id)}
      onManagedChange={(isManaged) => onManagedChange(e.id, isManaged)}
    />
  );

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {skills.length > 0 && (
            <Box>
              {/* fit-content keeps the toggle beside the title: SectionCollapseButton
                  carries `ml: 'auto'` for full-width dashboard headers, which would
                  otherwise push it to the far edge of the row. */}
              <Stack direction="row" alignItems="center" sx={{ width: 'fit-content' }}>
                <Typography variant="overline" color="text.secondary">
                  Analyses
                </Typography>
                <SectionCollapseButton
                  expanded={analysesExpanded}
                  onToggle={toggleAnalysesExpanded}
                  label="Analyses"
                />
              </Stack>
              <Collapse in={analysesExpanded}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.5,
                    mt: 0.5,
                  }}
                >
                  {sortedSkills.map((skill) => {
                    const isEntityScoped = skill.scope === 'ENTITY';
                    const topic = isEntityScoped ? undefined : typeTopics.find((t) => t.skillId === skill.id);
                    const entityRunCount = isEntityScoped
                      ? new Set(
                          topics
                            .filter((t) => t.skillId === skill.id && t.entityId != null && managedEntityIds.has(t.entityId))
                            .map((t) => t.entityId),
                        ).size
                      : undefined;
                    return (
                      <SkillCard
                        key={skill.id}
                        skill={skill}
                        topic={topic}
                        entityRunCount={entityRunCount}
                        totalEntityCount={managedEntityIds.size}
                        running={runningSkillId === skill.id}
                        disabled={runDisabled}
                        canRun={canRunSkills}
                        onRun={() => (isEntityScoped ? setEntityPickerSkill(skill) : onRunSkill(skill.id))}
                        onView={() => topic && onViewTopic(topic.id)}
                      />
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          )}

          <Box>
            {skills.length > 0 && (
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Entities
              </Typography>
            )}
            {entitiesLoading ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rounded" height={120} />
                ))}
              </Stack>
            ) : entities.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No {typeLabel.toLowerCase()} entities found for this project yet.
              </Typography>
            ) : layout === 'column' ? (
              <Box
                sx={{
                  display: 'grid',
                  // The inline panel takes a column's worth of width, so drop a column while it is open.
                  gridTemplateColumns:
                    selectedEntity && !narrow
                      ? { xs: '1fr', xl: 'repeat(2, 1fr)' }
                      : { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                {sortedEntities.map(renderCard)}
              </Box>
            ) : (
              <Stack spacing={2}>{sortedEntities.map(renderCard)}</Stack>
            )}
          </Box>
        </Box>

        {detailPanel && !narrow && (
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              // Stays in view while the card list scrolls. The page scrolls in
              // AppLayout's content column, whose visible height is the viewport
              // minus the 64px header spacer and the column's 24px bottom padding.
              position: 'sticky',
              top: 0,
              maxHeight: 'calc(100vh - 88px)',
            }}
          >
            {resizeHandle}
            <Box sx={{ width: panelWidth, overflowY: 'auto' }}>{detailPanel}</Box>
          </Box>
        )}
        {narrow && (
          <Drawer
            anchor="right"
            open={!!detailPanel}
            onClose={() => setSelectedEntityId(null)}
            PaperProps={{
              sx: { width: `min(${panelWidth}px, 100vw)`, flexDirection: 'row', overflow: 'hidden', py: 2, pr: 2 },
            }}
          >
            {resizeHandle}
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', pl: 1 }}>{detailPanel}</Box>
          </Drawer>
        )}
      </Stack>
      <SelectEntityDialog
        open={!!entityPickerSkill}
        skillName={entityPickerSkill?.name ?? ''}
        entities={entities}
        onClose={() => setEntityPickerSkill(null)}
        onConfirm={(entityId) => {
          if (entityPickerSkill) onRunSkill(entityPickerSkill.id, entityId);
          setEntityPickerSkill(null);
        }}
      />
    </>
  );
}
